import React, { useState, useEffect, useRef } from "react";
import { Input, Button, message, Modal } from "antd";
import {
  InfoCircleOutlined,
  ReloadOutlined,
  TeamOutlined,
} from "@ant-design/icons";
import { connect } from "react-redux";
import moment from "moment";
import {
  formatGroupReadLabel,
  parseThreadReadSummary,
} from "./evalAccountability";

const { TextArea } = Input;

const EMPTY_SUMMARY = {
  read: 0,
  total: 0,
  unseen: 0,
  seen: [],
  notSeen: [],
};

const POLL_MS = 30000;
const MAX_POLL_MS = 480000; // 8 minutes
const NEAR_BOTTOM_PX = 100;

const NameList = ({ title, names = [], dot }) => (
  <div className="py-3 first:pt-0 last:pb-0">
    <div className="flex items-center gap-1.5 mb-1.5">
      <span className={`h-1.5 w-1.5 rounded-full flex-shrink-0 ${dot}`} />
      <span className="text-[11px] font-semibold uppercase tracking-wide text-[#526B84]">
        {title}
      </span>
      <span className="text-[11px] text-[#A9B6C2]">{names.length}</span>
    </div>
    {names.length ? (
      <div className="flex flex-col">
        {names.map((name, i) => (
          <span
            key={`${name}-${i}`}
            className="text-[12px] leading-[22px] text-[#0F1A24]"
          >
            {name}
          </span>
        ))}
      </div>
    ) : (
      <span className="text-[12px] text-[#A9B6C2]">None</span>
    )}
  </div>
);

const latestMessageId = (list) => {
  if (!list?.length) return null;
  let maxId = null;
  for (const m of list) {
    const id = Number(m?.id);
    if (!Number.isNaN(id) && (maxId == null || id > maxId)) maxId = id;
  }
  return maxId;
};

const parseMessagesList = (res, data) =>
  res === true && data?.data?.data?.length > 0 ? data.data.data : [];

const isSameThreadTip = (nextList, prevList) =>
  (nextList?.length || 0) === (prevList?.length || 0) &&
  latestMessageId(nextList) === latestMessageId(prevList);

const isNearBottom = (el, threshold = NEAR_BOTTOM_PX) => {
  if (!el) return true;
  return el.scrollHeight - el.scrollTop - el.clientHeight <= threshold;
};

const nextPollDelay = (current) => Math.min(current * 2, MAX_POLL_MS);

const PlayChat = ({
  title,
  coach_depth_chart_id,
  play_id,
  game_id,
  coachProfile,
  getChatMessages,
  addChatMessage,
  markChatRead,
  getChatThreadReadSummary,
}) => {
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [readSummary, setReadSummary] = useState(EMPTY_SUMMARY);
  const [readsLoaded, setReadsLoaded] = useState(false);
  const [messagesLoaded, setMessagesLoaded] = useState(false);
  const [shouldPoll, setShouldPoll] = useState(false);
  const [readBreakdownOpen, setReadBreakdownOpen] = useState(false);
  const messagesContainerRef = useRef(null);
  const lastMarkedIdRef = useRef(null);
  const nearBottomRef = useRef(true);
  const prevMsgLenRef = useRef(0);
  const messagesRef = useRef([]);
  const shouldPollRef = useRef(false);
  const pollDelayRef = useRef(POLL_MS);
  const pollTimerRef = useRef(null);

  const hasFeedback = messages.length > 0;
  const groupReadLabel = !messagesLoaded
    ? null
    : !hasFeedback
      ? formatGroupReadLabel({ hasFeedback: false })
      : readsLoaded
        ? formatGroupReadLabel({ ...readSummary, hasFeedback: true })
        : null;

  const scrollToBottom = () => {
    if (!messagesContainerRef.current) return;
    messagesContainerRef.current.scrollTop =
      messagesContainerRef.current.scrollHeight;
  };

  useEffect(() => {
    messagesRef.current = messages;
  }, [messages]);

  useEffect(() => {
    shouldPollRef.current = shouldPoll;
  }, [shouldPoll]);

  useEffect(() => {
    const grew = messages.length > prevMsgLenRef.current;
    prevMsgLenRef.current = messages.length;
    if (grew || nearBottomRef.current) {
      scrollToBottom();
      nearBottomRef.current = true;
    }
  }, [messages]);

  const clearPollTimer = () => {
    if (pollTimerRef.current != null) {
      clearTimeout(pollTimerRef.current);
      pollTimerRef.current = null;
    }
  };

  const resetPollDelay = () => {
    pollDelayRef.current = POLL_MS;
  };

  const schedulePoll = () => {
    clearPollTimer();
    if (
      !shouldPollRef.current ||
      !coach_depth_chart_id ||
      !play_id ||
      !getChatMessages
    ) {
      return;
    }
    pollTimerRef.current = setTimeout(() => {
      fetchMessages({ showLoading: false });
    }, pollDelayRef.current);
  };

  const fetchThreadReadSummary = () => {
    if (
      !coach_depth_chart_id ||
      !play_id ||
      !game_id ||
      !getChatThreadReadSummary
    ) {
      return;
    }
    setSummaryLoading(true);
    getChatThreadReadSummary({
      params: { coach_depth_chart_id, play_id, game_id },
      callback: (res, data) => {
        if (res === true) setReadSummary(parseThreadReadSummary(data));
        setReadsLoaded(true);
        setSummaryLoading(false);
      },
    });
  };

  const markThreadRead = (messageId) => {
    if (!coach_depth_chart_id || !play_id || !game_id || !markChatRead) return;
    const tipKey = messageId == null ? 0 : messageId;
    if (tipKey === lastMarkedIdRef.current) return;
    markChatRead({
      body: {
        coach_depth_chart_id,
        play_id,
        game_id,
        ...(messageId != null ? { last_read_message_id: messageId } : {}),
      },
      callback: (res) => {
        if (res) lastMarkedIdRef.current = tipKey;
      },
    });
  };

  const applyMessages = (list) => {
    setMessages(list);
    messagesRef.current = list;
    if (list.length) {
      markThreadRead(latestMessageId(list));
      setShouldPoll(true);
      shouldPollRef.current = true;
    }
    setMessagesLoaded(true);
  };

  const onUnchangedMessages = ({ bump = true } = {}) => {
    if (bump) pollDelayRef.current = nextPollDelay(pollDelayRef.current);
    else resetPollDelay();
    setMessagesLoaded(true);
    if (shouldPollRef.current) schedulePoll();
  };

  const onChangedMessages = (list) => {
    resetPollDelay();
    const wasPolling = shouldPollRef.current;
    applyMessages(list);
    if (list.length) {
      // Effect schedules on false→true; only schedule here if already polling
      if (wasPolling) schedulePoll();
    } else {
      clearPollTimer();
      setShouldPoll(false);
      shouldPollRef.current = false;
    }
  };

  const fetchMessages = ({ showLoading = true, isManualRefresh = false } = {}) => {
    if (!coach_depth_chart_id || !play_id) return;

    if (showLoading) setLoading(true);
    if (isManualRefresh) setRefreshing(true);

    getChatMessages({
      params: {
        coach_depth_chart_id,
        play_id,
        game_id,
      },
      callback: (res, data, meta) => {
        const finish = () => {
          if (showLoading) setLoading(false);
          if (isManualRefresh) setRefreshing(false);
        };

        // Real 304 from axios (ETag / If-None-Match)
        if (meta?.status === 304) {
          onUnchangedMessages({ bump: !isManualRefresh });
          finish();
          return;
        }

        // Hard failure — do not wipe existing messages
        if (res !== true) {
          finish();
          return;
        }

        const list = parseMessagesList(res, data);
        // Browser may normalize 304 → 200 with cached body
        if (isSameThreadTip(list, messagesRef.current)) {
          onUnchangedMessages({ bump: !isManualRefresh });
          finish();
          return;
        }

        onChangedMessages(list);
        finish();
      },
    });
  };

  const handleRefreshMessages = () => {
    clearPollTimer();
    resetPollDelay();
    fetchMessages({ showLoading: true, isManualRefresh: true });
  };

  // Reset + initial fetch when thread keys change (no poll until messages exist)
  useEffect(() => {
    clearPollTimer();
    resetPollDelay();
    lastMarkedIdRef.current = null;
    nearBottomRef.current = true;
    prevMsgLenRef.current = 0;
    messagesRef.current = [];
    shouldPollRef.current = false;
    setReadSummary(EMPTY_SUMMARY);
    setReadsLoaded(false);
    setMessagesLoaded(false);
    setMessages([]);
    setShouldPoll(false);
    setSummaryLoading(false);
    setRefreshing(false);
    if (coach_depth_chart_id && play_id && getChatMessages) {
      fetchMessages();
      fetchThreadReadSummary();
    }
    return () => clearPollTimer();
  }, [coach_depth_chart_id, play_id, game_id]);

  // Keep timer in sync when shouldPoll flips (e.g. after send finds messages)
  useEffect(() => {
    if (!shouldPoll) {
      clearPollTimer();
      return undefined;
    }
    schedulePoll();
    return () => clearPollTimer();
  }, [shouldPoll, coach_depth_chart_id, play_id, game_id]);

  const handleSendMessage = () => {
    if (!newMessage.trim()) {
      message.warning("Message cannot be empty");
      return;
    }

    const messageText = newMessage;

    const demoNewMessage = {
      id: messages.length + 1,
      message: messageText,
      first_name: null,
      last_name: null,
      user_id: coachProfile?.user?.id || 999,
      created_at: null,
    };
    setMessages([...messages, demoNewMessage]);
    setNewMessage("");

    if (addChatMessage) {
      setLoading(true);
      addChatMessage({
        body: {
          message: messageText,
          coach_depth_chart_id,
          play_id,
          game_id,
          user: coachProfile?.user?.id,
        },
        callback: (res) => {
          if (res) {
            clearPollTimer();
            resetPollDelay();
            setShouldPoll(true);
            shouldPollRef.current = true;
            fetchMessages();
            message.success("Message sent");
          } else {
            message.error("Failed to send message");
          }
          setLoading(false);
        },
      });
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const refreshControl = (
    <button
      type="button"
      onClick={handleRefreshMessages}
      disabled={refreshing || loading}
      aria-label="Refresh messages"
      title="Refresh messages"
      className="inline-flex items-center gap-1 bg-transparent border-0 p-0 cursor-pointer disabled:cursor-not-allowed text-[11px] font-semibold text-[#526B84] hover:text-[#0F1A24] disabled:opacity-50"
    >
      <ReloadOutlined spin={refreshing} className="text-[12px]" />
      <span>Refresh</span>
    </button>
  );

  return (
    <div
      className="flex flex-col border border-[#EAEDF0] rounded-md bg-white mt-[10px]"
      style={{ height: "400px" }}
    >
      <div className="border-b border-[#EAEDF0] px-3 py-2 flex-shrink-0">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0">
            {title ? (
              <span className="text-[12px] font-inter font-semibold text-[#0F1A24] truncate">
                {title}
              </span>
            ) : null}
          </div>
          {groupReadLabel ? (
            <button
              type="button"
              onClick={() => {
                setReadBreakdownOpen(true);
                fetchThreadReadSummary();
              }}
              className={`flex items-center gap-1 text-[11px] font-semibold whitespace-nowrap bg-transparent border-0 p-0 cursor-pointer ${
                hasFeedback ? "text-[#ED780B]" : "text-black"
              }`}
              title="View who has read position group feedback"
            >
              <TeamOutlined />
              <span>{groupReadLabel}</span>
              <InfoCircleOutlined className="text-[#A9B6C2]" />
            </button>
          ) : (
            <span
              className="inline-block h-[12px] w-[72px] rounded bg-[#EAEDF0] animate-pulse flex-shrink-0"
              aria-hidden
            />
          )}
        </div>
      </div>
      <Modal
        title={
          <span className="text-[14px] font-semibold text-[#0F1A24]">
            Position Group Read Status
          </span>
        }
        open={readBreakdownOpen}
        onCancel={() => setReadBreakdownOpen(false)}
        footer={null}
        centered
        width={340}
      >
        <p className="text-[11px] leading-[18px] text-[#526B84] mb-4">
          Who reviewed group coaching points, including players not graded on
          this play.
        </p>
        <div className="divide-y divide-[#EAEDF0]">
          <NameList title="Seen" names={readSummary.seen} dot="bg-[#00B21E]" />
          <NameList
            title="Not Seen"
            names={readSummary.notSeen}
            dot="bg-[#A40E17]"
          />
        </div>
        <div className="mt-3 pt-3 border-t border-[#EAEDF0] flex justify-end">
          <button
            type="button"
            onClick={fetchThreadReadSummary}
            disabled={summaryLoading}
            aria-label="Refresh read status"
            className="inline-flex items-center gap-1.5 bg-transparent border-0 py-1 px-0.5 cursor-pointer disabled:cursor-not-allowed text-[12px] text-[#526B84] hover:text-[#0F1A24] disabled:opacity-50"
          >
            <ReloadOutlined spin={summaryLoading} className="text-[14px]" />
            <span>Refresh</span>
          </button>
        </div>
      </Modal>
      <div className="relative flex-1 min-h-0" style={{ maxHeight: "310px" }}>
        <div
          ref={messagesContainerRef}
          className="h-full overflow-y-auto p-3 pb-8 space-y-2"
          onScroll={() => {
            nearBottomRef.current = isNearBottom(messagesContainerRef.current);
          }}
        >
          {messages.length === 0 ? (
            <div className="text-center text-gray-400 py-8">
              <div>Talk to your position group here</div>
            </div>
          ) : (
            <div className="space-y-2">
              {messages.map((msg) => (
                <div
                  key={msg.id}
                  className={`flex ${
                    msg.user_id === coachProfile?.user?.id
                      ? "justify-end"
                      : "justify-start"
                  }`}
                >
                  <div
                    className={`max-w-[85%] ${
                      msg.user_id === coachProfile?.user?.id
                        ? "bg-[#1A91FF] text-white"
                        : "bg-[#EAEDF0] text-[#0F1A24]"
                    } rounded-lg px-2 py-1.5`}
                  >
                    {msg.user_id === coachProfile?.user?.id ? (
                      <p className="mb-0 text-xs whitespace-pre-wrap text-right">
                        {msg.message}
                        {msg.created_at && (
                          <>
                            {" "}
                            <span className="text-[10px] opacity-70 whitespace-nowrap">
                              {moment(msg.created_at).format("h:mm A")}
                            </span>
                          </>
                        )}
                      </p>
                    ) : (
                      <>
                        {msg.created_at && (
                          <div className="flex items-center gap-1.5  ">
                            {(msg.first_name || msg.last_name) && (
                              <span className="text-[10px] font-semibold text-capitalize">
                                {msg.first_name || "Coach"} {msg.last_name || ""}
                              </span>
                            )}
                            <span className="text-[10px] opacity-70">
                              {moment(msg.created_at).format("h:mm A")}
                            </span>
                          </div>
                        )}
                        <p className="mb-0 text-xs whitespace-pre-wrap">
                          {msg.message}
                        </p>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="absolute bottom-1.5 left-0 right-0 flex justify-center pointer-events-none z-10">
          <div className="pointer-events-auto rounded-full bg-white/90 px-2.5 py-1 shadow-sm ring-1 ring-[#EAEDF0]/80">
            {refreshControl}
          </div>
        </div>
      </div>
      <div className="border-t border-[#EAEDF0] p-2 flex-shrink-0">
        <div className="flex gap-2">
          <TextArea
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Type a message..."
            rows={2}
            className="flex-1 text-xs"
            style={{ fontSize: "12px" }}
          />
          <Button
            type="primary"
            onClick={handleSendMessage}
            loading={loading && !refreshing}
            className="bg-[#1A91FF] text-xs"
            size="small"
          >
            Send
          </Button>
        </div>
      </div>
    </div>
  );
};

const mapStateToProps = (state) => ({
  coachProfile: state?.app?.account?.profile?.data?.details,
});

const mapDispatchToProps = (dispatch) => ({
  getChatMessages: (details) =>
    dispatch(global.redux.action.depth_chart.getChatMessages(details)),
  addChatMessage: (details) =>
    dispatch(global.redux.action.depth_chart.addChatMessage(details)),
  markChatRead: (details) =>
    dispatch(global.redux.action.depth_chart.markChatRead(details)),
  getChatThreadReadSummary: (details) =>
    dispatch(global.redux.action.depth_chart.getChatThreadReadSummary(details)),
});

export default connect(mapStateToProps, mapDispatchToProps)(PlayChat);
